/**
 * Battery health score (0-100), grade, and status from snapshot data.
 * Authority: docs/implementation/BATTERY_HEALTH_TRACKING.md
 */

export interface HealthScoreInput {
  totalDegradationPercent: number | null;
  ageYears: number | null;
  degradationRatePerYear: number | null;
  degradationTrend90d?: number | null;
  cyclesCounted: number;
  estimatedTotalCycles: number | null;
}

export interface HealthScoreResult {
  score: number;
  grade: string;
  status: 'good' | 'fair' | 'degrading' | 'critical';
  summary: string;
}

const NORMAL_DEGRADATION_RATE = 2.5;
const GRADES = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D', 'F'];

export function computeBatteryHealthScore(input: HealthScoreInput): HealthScoreResult {
  const { totalDegradationPercent, ageYears, degradationRatePerYear } = input;
  let score = 100;
  let status: HealthScoreResult['status'] = 'good';
  let summary = 'Battery health is good.';

  if (totalDegradationPercent != null && ageYears != null && ageYears > 0) {
    const expectedDegradation = ageYears * NORMAL_DEGRADATION_RATE;
    const excess = totalDegradationPercent - expectedDegradation;
    score = Math.max(0, Math.min(100, 100 - excess * 20));
    if (score >= 80) {
      status = 'good';
      summary = 'Battery health is good. Degrading at normal rate for age.';
    } else if (score >= 60) {
      status = 'fair';
      summary = 'Battery health is fair. Slightly above normal degradation.';
    } else if (score >= 40) {
      status = 'degrading';
      summary = 'Battery is degrading faster than normal. Monitor and consider warranty documentation.';
    } else {
      status = 'critical';
      summary = 'Battery health is poor. Warranty claim or replacement may be warranted.';
    }
  }

  if (degradationRatePerYear != null && degradationRatePerYear > 5) {
    score = Math.min(score, 70);
    if (status === 'good') status = 'fair';
    summary = 'Degradation rate is above normal (2-3%/year). ' + summary;
  }

  const gradeIndex = Math.min(
    GRADES.length - 1,
    Math.max(0, Math.floor((100 - score) / (100 / GRADES.length)))
  );
  const grade = score >= 95 ? 'A+' : score >= 90 ? 'A' : GRADES[gradeIndex];

  return { score: Math.round(score), grade, status, summary };
}
